/* eslint-disable react/no-array-index-key */
import { useState, useEffect } from 'react';
import _ from 'lodash';
import { t } from '@lingui/macro';

import Properties, { renderIpAddrList, renderSiteList } from '../../properties/Properties';
import { useNetworkDevice, timeRangeFilterId, networkDeviceFilterId } from '../../filters';
import { getMsOf } from '../../../utilities/TimeRangeNavigationUtilities';
import { useFilterState } from '../../../stores/FilterStateStore';
import { useDataService } from '../../../services/ApiDataService';
import { stringProperty, listProperty, listLinkProperty } from '../../properties/funcs';
import { panelPropDefaults, panelPropTypes } from '../../page-framework/panel/PanelPropTypes';
import useStyles from '../../page-framework/panel/PanelStyles';
import Panel from '../../page-framework/panel/PanelDecorator';

const LOOPBACK_IP = '127.0.0.1';

const wiredNetworkDeviceTypes = {
    switch: t`Switch`,
    router: t`Router`,
    firewall: t`Firewall`,
    generic: t`Generic`,
    idrac: t`iDrac`,
    iot: t`IOT`,
    'packet-processor': t`Packet Processor`,
    'ups-psu': t`UPS/PSU`,
    kvm: t`KVM`,
    nas: t`NAS`,
    'raid-management': t`RAID Management`,
};

const propertiesColumns = [
    {
        properties: [
            stringProperty(t`Name`, 'sysName'),
            stringProperty(t`IP Address`, 'ipAddress'),
            stringProperty(t`Description`, 'sysDescription'),
            stringProperty(t`Type`, 'deviceType', renderDeviceType),
            stringProperty(t`Model`, 'deviceModel'),
            stringProperty(t`Vendor`, 'vendor'),
            stringProperty(t`Operating System`, 'operatingSystem'),
            stringProperty(t`Up Time`, 'upTime'),
            stringProperty(t`Contact`, 'contact'),
            stringProperty(t`MAC Address`, 'macAddress'),
            stringProperty(t`Serial Number`, 'serialNumber'),
            stringProperty(t`Location`, 'location'),
            listLinkProperty(t`Sites`, 'sites', [], [networkDeviceFilterId], renderSiteList),
        ],
    },
    {
        properties: [listProperty(t`IP Addresses`, 'ipAddresses', renderIpAddrList)],
    },
];

/**
 * Convert the array of property name/value pairs into an object, then convert deviceData ipAddresses/loopbackIps
 * into an array of objects suitable for PropertiesIpAddressList, and remove the localhost loopback from the list
 * if present. Add in sites from deviceData.
 * @param deviceInfo
 * @param deviceData
 * @return {*}
 */
function buildPropertiesData(deviceInfo, deviceData) {
    const properties = _(deviceInfo).keyBy('name').mapValues('value').value();

    const loopbacks = deviceData.loopbackIps || [];
    properties.ipAddresses = _.map(_.pull(deviceData.ipAddresses || [], LOOPBACK_IP), (ip) => {
        return { ipAddress: ip, loopback: loopbacks.indexOf(ip) >= 0 };
    });
    properties.sites = deviceData?.sites || [];

    return properties;
}

function renderDeviceType(value) {
    return wiredNetworkDeviceTypes[value] || t`Unknown`;
}

/**
 * Combine data from serverinfo and device queries
 * @returns {Object}
 */
function usePropertiesData() {
    const networkDevice = useNetworkDevice();
    const [filterState] = useFilterState();
    const dataService = useDataService();
    const [props, setProps] = useState();
    const timeRange = filterState?.current?.[timeRangeFilterId];
    const deviceId = networkDevice?.uuid;
    const start = timeRange?.start;
    const end = timeRange?.end;

    useEffect(() => {
        if (!deviceId || !start) {
            return;
        }
        const serverInfoParams = {
            start: getMsOf(start),
            end: getMsOf(end),
            type: 'device',
            device: deviceId,
            metrics: ['status'],
        };
        const serverInfoRequest = dataService.get('/query/serverinfo', serverInfoParams);
        const deviceRequest = dataService.get(`/ngp/v2/devices/${deviceId}`);

        Promise.all([serverInfoRequest, deviceRequest]).then((resolved) => {
            const [deviceInfo, deviceData] = resolved;
            setProps(buildPropertiesData(deviceInfo?.data, deviceData));
        });
    }, [dataService, deviceId, start, end]);

    return props;
}

export function NetworkDeviceProperties({ data }) {
    const classes = useStyles();
    return (
        <div className={classes.propertiesPanel}>
            {propertiesColumns.map((def, index) => (
                <Properties propertiesDef={def} data={data} key={`column-${index}`} />
            ))}
        </div>
    );
}

export function NetworkDevicePropertiesPanel() {
    const properties = usePropertiesData();
    if (!properties) return null;
    return <NetworkDeviceProperties data={properties} />;
}

NetworkDevicePropertiesPanel.propTypes = panelPropTypes;

NetworkDevicePropertiesPanel.defaultProps = {
    ...panelPropDefaults,
    title: t`Network Device Properties`,
    autoHeight: true,
    hideActions: true,
    minContentHeight: 200,
};

export default Panel({
    typeAlias: 'DeviceAttributes',
    filters: {
        required: [timeRangeFilterId, networkDeviceFilterId],
        optional: [],
    },
})(NetworkDevicePropertiesPanel);
